import { Box, ListItemText, Stack, Typography, Skeleton } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { DashboardSectionHeader } from "../DashboardSectionHeader"
import { faCalendar } from "@fortawesome/pro-regular-svg-icons"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { MeetingList } from "./MeetingList"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { ErrorDisplay } from "../../../../../../core/api/errorTypes"
import { fetchGuests, ServiceCenterGuestListQuery } from "../../../guestList/guestListApi"
import type { ServiceCenterGuestListResponse } from "../../../guestList/guestListApi"
import { RefetchIntervals } from "../../../../../../core/api/refetchIntervals"
import {
    fetchServiceCenterMeetings,
    ServiceCenterHomeQuery,
    ServiceCenterMeetingsResponse,
} from "../../serviceCenterHomeApi"
import { useExportMeetings } from "../../hooks/useExportMeetings"
import { MeetingDrawer } from "../drawer/MettingDrawer"
import { useCallback, useState } from "react"
import { ServiceCenterBooking } from "../../serviceCenterHomeTypes"
import { ErrorBoundary } from "react-error-boundary"
import { isEmpty } from "lodash"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { UserDashboardErrorFallbackCard } from "../../../../../nonAdminUser/reports/home/components/UserDashboardErrorFallbackCard"
import { MeetingDatePicker } from "./MeetingDatePicker"
import { DateTime } from "luxon"
import { FilterSearch } from "../../../../../../shared/components/createOrder/components/FilterSearch"
import { StandardSquareFilter } from "../../../../../../shared/components/filters/squareFilter/StandardSquareFilter"
import { MeetingAudienceFilterType } from "../../../../../../shared/components/filters/orderTypeFilter/helpers"
import { useCurrentServiceCenter } from "../../../../store/serviceCenterHooks"
import { useDateTime } from "../../../../../localization/useDateTime"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { HIDDEN_IN_PRINT } from "../../../../../../shared/components/print/printPageOnly"
import { usePrintPageOnly } from "../../../../../../shared/hooks/usePrintPageOnly"
import { HomePrintSection, printHomeSection } from "../homePrintMode"
import { PrintExportMenu } from "../PrintExportMenu"
import { PrintBookingsList } from "./PrintBookingsList"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import { BookingResourceTabs } from "./BookingResourceTabs"
import {
    EMPTY_BOOKING_TAB_COUNTS,
    loadMeetingAudienceFilter,
    saveMeetingAudienceFilter,
    toBookingDisplayRow,
    toMeetingArrivals,
    toMeetingSectionView,
} from "./helpers"

const useStyles = makeStyles()((theme) => ({
    card: {
        backgroundColor: "white",
        borderRadius: "0.75rem",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
        padding: "1.5rem",
    },
    skeletonCard: {
        height: "2rem",
        marginBottom: ".5rem",
        borderRadius: ".5rem",
    },
    noDataContainer: {
        padding: "2rem 2rem 2rem 0",
    },
    hiddenInPrint: HIDDEN_IN_PRINT,
    searchSlot: {
        position: "relative",
        width: "2.5rem",
        height: "2.5rem",
        flexShrink: 0,
    },
    searchOverlay: {
        position: "absolute",
        top: 0,
        right: 0,
        zIndex: 1,
        borderRadius: ".75rem",
        backgroundColor: theme.palette.common.white,
        "& .MuiOutlinedInput-root:not(.Mui-focused)": {
            boxShadow: "none",
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
            },
            "& svg": {
                color: theme.palette.tailwind.slate[400],
            },
            "&:hover": {
                backgroundColor: theme.palette.tailwind.slate[100],
            },
        },
    },
    exportButton: {
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: ".625rem",
        fontSize: "1.25rem",
        color: theme.palette.tailwind.slate[400],
        "&:hover, &[aria-expanded='true']": {
            color: theme.palette.tailwind.slate[600],
            backgroundColor: theme.palette.tailwind.slate[100],
        },
    },
    audienceLabel: {
        color: theme.palette.tailwind.slate[500],
    },
}))

const MEETING_AUDIENCE_FILTER_OPTIONS = Object.values(MeetingAudienceFilterType)

const selectArrivals = (data: ServiceCenterGuestListResponse) => toMeetingArrivals(data.guests)

function SafeMeetingSection() {
    const { classes, cx } = useStyles()
    const { serviceCenterId } = useCurrentServiceCenter()
    const [selectedMeeting, setSelectedMeeting] = useState<ServiceCenterBooking | null>(null)
    const [searchQuery, setSearchQuery] = useState("")
    const [resourceType, setResourceType] = useState<ResourceType>(ResourceType.ROOM)
    const printPageOnly = usePrintPageOnly()

    const isMeetingDetailsDrawerOpen = selectedMeeting !== null

    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const [selectedDate, setSelectedDate] = useState<DateTime | null>(dateTimeFactory.now())
    const [audienceFilter, setAudienceFilter] = useState(loadMeetingAudienceFilter)

    const handleDateChange = (newDate: DateTime | null) => {
        setSelectedDate(newDate)
    }

    const handleAudienceFilterChange = (filter: MeetingAudienceFilterType) => {
        setAudienceFilter(filter)
        saveMeetingAudienceFilter(filter)
    }

    const selectView = useCallback(
        (data: ServiceCenterMeetingsResponse) => {
            const sectionView = toMeetingSectionView(data.orders, {
                search: searchQuery,
                resourceType,
                audience: audienceFilter,
            })
            return {
                ...sectionView,
                displayRows: sectionView.rows.map((row) => toBookingDisplayRow(row, l10n, dateTimeFactory)),
            }
        },
        [searchQuery, resourceType, audienceFilter, l10n, dateTimeFactory],
    )

    const { data: view, isLoading } = useApiQuery(fetchServiceCenterMeetings, {
        queryName: ServiceCenterHomeQuery.SERVICE_CENTER_MEETINGS,
        dependencies: {
            serviceCenterId: serviceCenterId,
            startDate: selectedDate?.toISODate() ?? "",
        },
        select: selectView,
        refetchInterval: RefetchIntervals.short,
    })

    const selectedDateIso = selectedDate?.toISODate() ?? ""
    const { data: arrivals } = useApiQuery(fetchGuests, {
        queryName: ServiceCenterGuestListQuery.SERVICE_CENTER_GUEST_LIST,
        dependencies: { serviceCenterId, startDate: selectedDateIso, endDate: selectedDateIso },
        enabled: Boolean(selectedDateIso),
        select: selectArrivals,
        errorDisplay: ErrorDisplay.NONE,
        refetchInterval: RefetchIntervals.short,
    })

    const meetings = view?.meetings
    const filteredMeetings = view?.rows
    const displayRows = view?.displayRows ?? []
    const handleExport = useExportMeetings({ rows: displayRows, date: selectedDate })
    const handlePrint = () => printHomeSection(HomePrintSection.BOOKINGS, printPageOnly)

    return (
        <Box flex={1}>
            <Box className={cx(classes.card, classes.hiddenInPrint)}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap=".5rem"
                    marginBottom="1rem"
                >
                    <DashboardSectionHeader icon={faCalendar}>
                        <LocalizedStrict id="service-center-reports-dashboard-bookings-title">
                            <Typography variant="h6" fontWeight="bold">
                                Bookings
                            </Typography>
                        </LocalizedStrict>
                    </DashboardSectionHeader>

                    <Stack direction="row" justifyContent="end" alignItems="center" spacing=".5rem" flexShrink={0}>
                        <MeetingDatePicker value={selectedDate} onChange={handleDateChange} />
                        <Box className={classes.searchSlot}>
                            <FilterSearch
                                search={searchQuery}
                                setSearch={setSearchQuery}
                                activeWidth="14rem"
                                collapseWithValue
                                className={classes.searchOverlay}
                            />
                        </Box>
                        <PrintExportMenu
                            disabled={isLoading}
                            buttonClassName={classes.exportButton}
                            printLabel={
                                <LocalizedStrict id="export-button-print">
                                    <ListItemText>Print</ListItemText>
                                </LocalizedStrict>
                            }
                            exportLabel={
                                <LocalizedStrict id="service-center-reports-dashboard-bookings-export-menu-csv">
                                    <ListItemText>Export to CSV</ListItemText>
                                </LocalizedStrict>
                            }
                            onPrint={handlePrint}
                            onExportCsv={handleExport}
                        />
                    </Stack>
                </Stack>

                <Box marginBottom="1rem">
                    <BookingResourceTabs
                        value={resourceType}
                        counts={view?.tabCounts ?? EMPTY_BOOKING_TAB_COUNTS}
                        isLoading={isLoading}
                        onChange={setResourceType}
                    />
                </Box>

                {resourceType === ResourceType.ROOM && view?.hasMeetings && (
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap="1rem"
                        marginBottom=".75rem"
                    >
                        <LocalizedStrict id="service-center-reports-dashboard-meeting-audience-filter-label">
                            <Typography variant="subtitle1" className={classes.audienceLabel}>
                                Show meetings
                            </Typography>
                        </LocalizedStrict>

                        <StandardSquareFilter
                            value={audienceFilter}
                            onChange={handleAudienceFilterChange}
                            options={MEETING_AUDIENCE_FILTER_OPTIONS}
                            translationBase="standard-order-type-filter-mode"
                        />
                    </Stack>
                )}

                {isLoading ? (
                    <>
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                    </>
                ) : !isEmpty(filteredMeetings) ? (
                    <ErrorBoundary fallback={<UserDashboardErrorFallbackCard />}>
                        <MeetingList
                            selectedMeeting={selectedMeeting}
                            onMeetingClick={(item) => setSelectedMeeting(item.id === selectedMeeting?.id ? null : item)}
                            meetings={filteredMeetings}
                            arrivals={arrivals}
                        />
                    </ErrorBoundary>
                ) : (
                    <NoBookingsCard resourceType={resourceType} />
                )}

                <MeetingDrawer
                    orderId={selectedMeeting?.id}
                    isOpen={isMeetingDetailsDrawerOpen}
                    meetings={meetings}
                    onClose={() => setSelectedMeeting(null)}
                />
            </Box>
            <PrintBookingsList
                rows={displayRows}
                tab={l10n.getStringForEnum("service-center-reports-dashboard-bookings-tab", resourceType, resourceType)}
                date={selectedDate ? dateTimeFactory.formatDate(selectedDate) : ""}
                resourceType={resourceType}
            />
        </Box>
    )
}

export function MeetingSection() {
    return (
        <QueryErrorBoundary Fallback={MeetingErrorFallback}>
            <SafeMeetingSection />
        </QueryErrorBoundary>
    )
}

function MeetingErrorFallback() {
    const { classes } = useStyles()
    return (
        <Box flex={1}>
            <Box className={classes.card}>
                <DashboardSectionHeader icon={faCalendar}>
                    <LocalizedStrict id="service-center-reports-dashboard-bookings-title">
                        <Typography variant="h6" fontWeight="bold">
                            Bookings
                        </Typography>
                    </LocalizedStrict>
                </DashboardSectionHeader>
                <NoMeetingsCard />
            </Box>
        </Box>
    )
}

function NoMeetingsCard() {
    const { classes } = useStyles()
    return (
        <Box className={classes.noDataContainer}>
            <LocalizedStrict id="service-center-reports-dashboard-no-meetings">
                <Typography variant="subtitle1" color="#666666">
                    There are no meetings today.
                </Typography>
            </LocalizedStrict>
        </Box>
    )
}

function NoBookingsCard({ resourceType }: Readonly<{ resourceType: ResourceType }>) {
    const { classes } = useStyles()
    return (
        <Box className={classes.noDataContainer}>
            <LocalizedEnum base="service-center-reports-dashboard-bookings-empty" enumValue={resourceType}>
                <Typography variant="subtitle1" color="text.secondary">
                    No bookings on this day
                </Typography>
            </LocalizedEnum>
        </Box>
    )
}
